/**
 * Data import/export guide (Excel) (AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { FileSpreadsheet } from "@fleet/icons";

import type { HelpGuide } from "../types";
import { GoTo, Note, Steps } from "../components/help-prose";

export const ioGuide: HelpGuide = {
  key: "help.io",
  slug: "importar",
  title: msg`Import your data (Excel)`,
  description: msg`Bring your existing records — students, leads, payment history — from Excel, and export anything the platform manages.`,
  audience: msg`Administrators and data managers`,
  icon: FileSpreadsheet,
  sections: [
    {
      id: "panorama",
      title: msg`What can be imported and exported`,
      summary: msg`Every major entity is registered in the import/export hub: students, leads, courses, cohorts, schedules, enrollments, attendance, gradebook and payments. You only see entities your role allows.`,
      body: () => (
        <>
          <GoTo to="/admin/import-export" label={msg`Open the Import/Export hub`} />
        </>
      ),
    },
    {
      id: "exportar",
      title: msg`Exporting`,
      summary: msg`Any entity exports to .xlsx or .csv with your current filters. The cohort workbook bundles roster, attendance, grades and payments in one multi-sheet file.`,
      body: () => (
        <>
          <GoTo to="/admin/import-export" label={msg`Export data`} />
        </>
      ),
    },
    {
      id: "importar",
      title: msg`The import wizard`,
      summary: msg`Import runs in safe steps: upload, column mapping with auto-match, a dry-run preview with per-row issues, and only then apply.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Upload your .xlsx or .csv file.`,
              msg`Map your columns to the platform's fields — the wizard auto-matches by name and shows an example per column.`,
              msg`Review the dry-run: every row is validated and issues are listed per row before anything is written.`,
              msg`Apply in partial mode (skip bad rows) or atomic mode (all or nothing). A downloadable error report lists rejected rows.`,
            ]}
          />
          <GoTo to="/admin/import-export" label={msg`Start an import`} />
        </>
      ),
    },
    {
      id: "idempotencia",
      title: msg`Re-running an import safely`,
      summary: msg`Imports are idempotent: rows are matched by a natural key (students by cédula or email), so re-importing updates instead of duplicating.`,
      body: () => (
        <>
          <Note
            title={msg`Migrating your academy`}
            msg={msg`This is the recommended path to migrate your current records: import students first, then enrollments, then payment history. Each run is recorded in the activity log with its batch.`}
          />
          <GoTo to="/admin/import-export" label={msg`Open the Import/Export hub`} />
        </>
      ),
    },
  ],
};
